import React, { useEffect, useState } from "react";
import {
  getCaseLevelBillingChangeLog,
  getCaseLevelBillingDetails,
} from "../../../../../services/BillingConfigApi";
import { formatAppointmentDateTime } from "../../../../../utils/dateUtils";
import AccordionComponent from "../../../../../common/Accordion";
import ChangeLogDrawer from "./ChangeLogDrawer";
import BillingDataTable from "../BillingDataTable";
import { BILLINGCASE_CONFIG_CHANGE_LOG } from "../../actions/constant";
import {
  Button,
  Col,
  Form,
  OverlayTrigger,
  Row,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from "react-bootstrap";
import SearchableSingleSelectDropdown from "../../../../../common/SearchableDropdown";
import NumberInput from "../../../../../common/NumberInput";
import ThresholdEditableTable from "./PageThresholdEditable";
import ThresholdLineEditable from "./LineThresholdEditable";
import MultilineTextBox from "../../../../../common/MultilineTextBox";

const ViewCaseBillingConfig = ({ onClose, selectedData }: any) => {
  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [rateType, setRateType] = useState("Fixed");
  const [currencyOptions, setCurrencyOptions] = useState<any[]>([]);
  const [selectedCurrency, setSelectedCurrency] = useState("");
  const [selectedCurrencyLabel, setSelectedCurrencyLabel] = useState("");
  const [fixedAmount, setFixedAmount] = useState("");
  const [isInputPages, setIsInputPages] = useState<any>("Input");
  const [thresholdPageData, setThresholdPageData] = useState<any[]>([]);
  const [thresholdLineData, setThresholdLineData] = useState<any[]>([]);

  const [thresholdErrors, setThresholdErrors] = useState<string[]>([]);
  const [thresholdLineErrors, setThresholdLineErrors] = useState<string[]>([]);
  const [isActive, setIsActive] = useState(true);
  const [comments, setComments] = useState("");
  const [changeLogData, setChangeLogData] = useState<any[]>([]);
  const [showTooltip, setShowTooltip] = useState(false);
  const [showChangeDrawer, setShowChangeDrawer] = useState(false);

  const [createdOn, setCreatedOn] = useState("");
  const [createdBy, setCreatedBy] = useState("");

  const currencyData = [
    {
      label: "USD",
      value: "USD",
    },
    {
      label: "INR",
      value: "INR",
    },
  ];

  const parsePageSlabsText = (text: string) => {
    if (!text) return { slabs: [], pageType: "Input" as "Input" | "Output" };

    const rows = text
      .split("|")
      .map((r) => r.trim())
      .filter(Boolean);

    const slabs = rows.map((row) => {
      const [isInput, min, max, rate] = row.split(",");

      return {
        isInputPage: Number(isInput) as 0 | 1,
        min: Number(min),
        max: Number(max),
        rate: Number(rate),
      };
    });

    const pageType = slabs[0]?.isInputPage === 1 ? "Input" : "Output";

    return { slabs, pageType };
  };

  const parseThresholdLineString = (data?: string) => {
    if (!data) return [];

    return data.split("|").map((chunk) => {
      const [min, max, rate] = chunk.trim().split(",");

      return {
        min: Number(min),
        max: Number(max),
        rate: Number(rate),
      };
    });
  };

  const getBillingDetails = async () => {
    try {
      const billingData = await getCaseLevelBillingDetails(
        selectedData.billingCaseConfigId,
      );

      if (billingData) {
        if (billingData.billingForUserFullName) {
          setSelectedUser({
            userId: billingData.userId ?? null, // if available
            tenantId: billingData.tenantId ?? null,
            usfUlNameRole: billingData.billingForUserFullName,
          });
        }

        setCurrencyOptions(currencyData);

        const getRateType =
          billingData.rateType == "Fixed" && billingData.flatRate > 0
            ? "Fixed"
            : billingData.rateType == "Fixed" && billingData.flatRatePerPage > 0
              ? "Fixed Per Page"
              : billingData.rateType == "Fixed" &&
                  billingData.flatRatePerLine > 0
                ? "Fixed Per Line"
                : billingData.rateType == "PER_PAGE"
                  ? "Per Page"
                  : billingData.rateType == "PER_LINE"
                    ? "Per Line"
                    : "";

        setRateType(getRateType);

        setSelectedCurrency(
          billingData.currencyType ? billingData.currencyType : null,
        );
        setSelectedCurrencyLabel(
          billingData.currencyType ? billingData.currencyType : null,
        );

        const getFixedAmount =
          billingData.rateType == "Fixed" && billingData.flatRate
            ? billingData.flatRate
            : billingData.rateType == "Fixed" && billingData.flatRatePerPage
              ? billingData.flatRatePerPage
              : billingData.rateType == "Fixed" && billingData.flatRatePerLine
                ? billingData.flatRatePerLine
                : "";
        setFixedAmount(getFixedAmount > 0 ? getFixedAmount : "");

        const { slabs, pageType } = parsePageSlabsText(
          billingData.page_slabs_text,
        );

        setIsInputPages(pageType);
        setThresholdPageData(slabs);

        const parsed = parseThresholdLineString(billingData.line_slabs_text);
        setThresholdLineData(parsed);

        setIsActive(billingData.isActive == 1 ? true : false);

        setComments(billingData.comments ? billingData.comments : null);

        setCreatedOn(
          billingData.createdDate
            ? formatAppointmentDateTime(billingData.createdDate)
            : "",
        );

        setCreatedBy(
          billingData.createdByName ? billingData.createdByName : "",
        );

        const logData = await getCaseLevelBillingChangeLog(
          selectedData.billingCaseConfigId,
        );

        const filteredLogData = logData?.filter(
          (item: any) =>
            !["modifieddate", "modifiedby"].includes(
              item.fieldName?.toLowerCase(),
            ),
        );

        setChangeLogData(filteredLogData);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  useEffect(() => {
    if (selectedData) {
      getBillingDetails();
    }
  }, [selectedData]);

  const handleOpenChangeDrawer = async () => {
    if (selectedData) {
      setShowTooltip(false);
      setShowChangeDrawer(true);
    }
  };

  const handleCloseChangeDrawer = () => {
    setShowChangeDrawer(false);
  };

  const [configurationBillingKey, setConfigurationBillingKey] = useState<
    string | null
  >("0");

  const [pageThresholdKey, setPageThresholdKey] = useState<string | null>("0");

  const handleAccordionSelect = (key: string | null) => {
    // Prevent collapsing if the same key is clicked
    if (configurationBillingKey === key) {
      return; // do nothing
    }
    setConfigurationBillingKey(key || "0");
  };

  const configurationAccordion = (
    items: { title: string; content: JSX.Element }[],
  ) => (
    <AccordionComponent
      items={items.map((item) => ({ ...item }))}
      activeKey={configurationBillingKey}
      onSelect={handleAccordionSelect}
    />
  );

  const thresholdAccordion = (
    items: { title: string; content: JSX.Element }[],
  ) => (
    <AccordionComponent
      items={items.map((item) => ({ ...item }))}
      activeKey={pageThresholdKey}
      onSelect={(key) => setPageThresholdKey(key as string)}
    />
  );

  const fixedSelected = ["Fixed", "Fixed Per Page", "Fixed Per Line"].includes(
    rateType,
  );

  return (
    <div className="mt-4 mb-5">
      {configurationAccordion([
        {
          title: "Configure Billing",
          content: (
            <>
              <Row className="align-items-center mt-0">
                <div className="col-md-4 col-xxl-4">
                  <Col>
                    <div className="form-floating">
                      <input
                        type="text"
                        name="User Name"
                        className="form-control zipcodeDetailsOverlay"
                        placeholder="Search User"
                        maxLength={50}
                        value={selectedUser ? selectedUser.usfUlNameRole : ""}
                        autoComplete="off"
                        required
                        disabled={!!selectedData}
                      />
                      <label>
                        User Name <span className="text-danger">*</span>
                      </label>
                    </div>
                  </Col>
                </div>
              </Row>
              <Row className="align-items-center mt-3">
                <div className="col-md-6 col-xxl-4">
                  <div className="rate-type-wrapper">
                    {/* Fixed */}
                    <div className="rate-type-block adjust_spacingToggle">
                      <div className="rate-type-label">
                        Rate Type (Fixed)
                        <span style={{ color: "#0092d5" }}>
                          {fixedSelected ? " Selected" : ""}
                        </span>
                      </div>

                      <ToggleButtonGroup
                        type="radio"
                        name="rateTypeMain"
                        value={rateType}
                        className="appointment-ui adjust_pills_xs"
                      >
                        <ToggleButton
                          id="fixed-rate"
                          variant="outline-secondary"
                          value="Fixed"
                          className="px-3"
                        >
                          Fixed
                        </ToggleButton>

                        <ToggleButton
                          id="per-page-main"
                          variant="outline-secondary"
                          value="Fixed Per Page"
                          className="px-3"
                        >
                          Per Page
                        </ToggleButton>

                        <ToggleButton
                          id="per-line-main"
                          variant="outline-secondary"
                          value="Fixed Per Line"
                          className="px-3"
                        >
                          Per Line
                        </ToggleButton>
                      </ToggleButtonGroup>
                    </div>

                    {/* Pipe */}
                    <div className="rate-type-pipe">||</div>

                    {/* Slab */}
                    <div className="rate-type-block">
                      <div className="rate-type-label">
                        Rate Type (Slab)
                        <span style={{ color: "#0092d5" }}>
                          {!fixedSelected ? " Selected" : ""}
                        </span>
                      </div>

                      <ToggleButtonGroup
                        type="radio"
                        name="rateTypeSlab"
                        value={rateType}
                        className="appointment-ui adjust_pills_xs"
                      >
                        <ToggleButton
                          id="per-page-slab"
                          variant="outline-secondary"
                          value="Per Page"
                          className="px-3"
                        >
                          Per Page
                        </ToggleButton>

                        <ToggleButton
                          id="per-line-slab"
                          variant="outline-secondary"
                          value="Per Line"
                          className="px-3"
                        >
                          Per Line
                        </ToggleButton>
                      </ToggleButtonGroup>
                    </div>
                  </div>
                </div>

                <Col sm={3} className="selection_dropdown">
                  <SearchableSingleSelectDropdown
                    label="Currency"
                    value={selectedCurrency}
                    options={currencyOptions}
                    name="currencyID"
                    required={true}
                    disabled={selectedData}
                  />
                </Col>
                {rateType == "Fixed" ||
                rateType == "Fixed Per Page" ||
                rateType == "Fixed Per Line" ? (
                  <Col sm={3}>
                    <NumberInput
                      label="Amount"
                      name="amount"
                      value={fixedAmount}
                      onChange={(e) => setFixedAmount(e.target.value)}
                      allowDecimal={true}
                      limit={[8, 2]}
                      showAmountLabel={true}
                      amountLabel={selectedCurrencyLabel}
                      disabled={selectedData}
                    />
                  </Col>
                ) : null}
                {rateType === "Per Page" && (
                  <>
                    <Col
                      sm="3"
                      className="fw-semibold ps-1 form-label-fixed d-flex align-items-center"
                    >
                      Slab Apply on Pages
                      <ToggleButtonGroup
                        type="radio"
                        name="inputPagesType"
                        value={isInputPages}
                        className="appointment-ui adjust_pills_xs"
                      >
                        <ToggleButton
                          id="input-page"
                          variant="outline-secondary"
                          value="Input"
                          className="px-3"
                          disabled={selectedData}
                        >
                          Input
                        </ToggleButton>

                        <ToggleButton
                          id="output-page"
                          variant="outline-secondary"
                          value="Output"
                          className="px-3"
                          disabled={selectedData}
                        >
                          Output
                        </ToggleButton>
                      </ToggleButtonGroup>
                    </Col>
                  </>
                )}
              </Row>
              {rateType == "Per Page" ? (
                <Row className="align-items-center mt-3">
                  {thresholdAccordion([
                    {
                      title:
                        isInputPages === "Input"
                          ? "Input Page Slab"
                          : "Output Page Slab",
                      content: (
                        <ThresholdEditableTable
                          isInputPages={isInputPages}
                          value={thresholdPageData}
                          canAdd={false}
                          onChange={setThresholdPageData}
                          onValidationError={setThresholdErrors}
                        />
                      ),
                    },
                  ])}
                </Row>
              ) : null}
              {rateType == "Per Line" ? (
                <Row className="align-items-center mt-3">
                  {thresholdAccordion([
                    {
                      title: "Line Slab",
                      content: (
                        <ThresholdLineEditable
                          value={thresholdLineData}
                          canAdd={false}
                          onChange={setThresholdLineData}
                          onValidationError={setThresholdLineErrors}
                        />
                      ),
                    },
                  ])}
                </Row>
              ) : null}
              <Row className="align-items-center mt-3">
                <div className="col-md-4 col-sm-6">
                  <div className="d-flex align-items-center toggle-btn">
                    <span className="me-3 fw-semibold">Status</span>

                    <Form.Check
                      type="switch"
                      id="sort-switch"
                      checked={isActive}
                      disabled={selectedData}
                    />
                    <span className="ms-2">
                      {isActive ? "Active" : "In Active"}
                    </span>
                  </div>
                </div>
              </Row>
              <Col
                sm={12}
                className="mt-3 notemultiline notemultilinerushnote  mb-3"
              >
                <MultilineTextBox
                  label="Comments"
                  name="comments"
                  value={comments}
                  onChange={(e) => {
                    let value = e.target.value;
                    if (value.startsWith(" ")) return;
                    value = value?.trimStart();
                    value = value.replace(/ {2,}/g, " ");
                    setComments(value);
                  }}
                  maxLength={100}
                  disabled={selectedData}
                />
              </Col>
              <Row className="mt-3 mb-3">
                <Col sm={12} className="d-flex align-items-center">
                  {changeLogData?.length > 0 && selectedData && (
                    <OverlayTrigger
                      show={showTooltip}
                      placement="auto"
                      trigger={["hover", "focus"]}
                      delay={{ show: 200, hide: 100 }}
                      overlay={
                        <Tooltip style={{ backgroundColor: "#b3e0ff" }}>
                          Logs
                        </Tooltip>
                      }
                    >
                      <div className="d-flex">
                        <Button
                          type="button"
                          className="btn btn-primary d-flex align-items-center mx-2"
                          onClick={handleOpenChangeDrawer}
                          onMouseEnter={() => setShowTooltip(true)}
                          onMouseLeave={() => setShowTooltip(false)}
                        >
                          <span className="material-icons">description</span>
                        </Button>
                      </div>
                    </OverlayTrigger>
                  )}

                  <Button
                    variant="outline-info"
                    className="d-flex align-items-center me-auto btn-text-border"
                    onClick={onClose}
                  >
                    <span className="material-icons me-1">arrow_back</span>
                    Back
                  </Button>
                </Col>
              </Row>
            </>
          ),
        },
      ])}

      <ChangeLogDrawer
        title="Change Log"
        open={showChangeDrawer}
        onClose={handleCloseChangeDrawer}
        placement="bottom"
        data={changeLogData}
        createdOn={createdOn}
        createdBy={createdBy}
      >
        <BillingDataTable
          headers={BILLINGCASE_CONFIG_CHANGE_LOG}
          rows={changeLogData}
          isLogTable={true}
        />
      </ChangeLogDrawer>
    </div>
  );
};

export default ViewCaseBillingConfig;
